import { css, keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { Popover } from '@mui/material'
import { useState } from 'react'

import { Colors } from 'src/components/theme'
import { PropertiesDistributionItem } from 'src/v2/components/properties-distribution-item'
import { PropertyDistributionItem } from 'src/v2/views/owner-reveal/sidebar/result-overlay/properties-distribution-list'

const pulsatingAnimation = keyframes`
0% { opacity: 0.25; }
50% { opacity: 1; }
100% { opacity: 0.25; }
`

const Container = styled.div<{ backgroundColor: string; height?: number; maxWidth?: number }>`
  overflow: hidden;
  display: flex;
  flex: 1;
  height: ${({ height }) => height || 10}px;
  max-width: ${({ maxWidth }) => (maxWidth ? `${maxWidth}px` : 'auto')};
  border-radius: 5px;
  background-color: ${({ backgroundColor }) => backgroundColor};
`

const ProgressBar = styled.div<{ color: string; percentage: number; pulsating?: boolean }>`
  width: ${({ percentage }) => percentage}%;
  height: 100%;
  background-color: ${({ color }) => color};

  ${({ pulsating }) =>
    pulsating &&
    css`
      animation: ${pulsatingAnimation} 2s ease infinite;
    `}
`

const PropertiesDistributionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  width: fit-content;
  /* min-width: 220px; */
`

type PercentageBarProps = {
  loading?: boolean
  backgroundColor?: string
  percentages?: Record<string, number>
  height?: number
  maxWidth?: number
  showDetails?: boolean
  /**only required if you wanna show details */
  propertiesDistributions?: PropertyDistributionItem[]
}

export const PercentageBar = ({
  loading = false,
  backgroundColor = Colors.neutral100,
  percentages = {},
  height,
  maxWidth,
  showDetails = false,
  propertiesDistributions = [],
}: PercentageBarProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)
  const open = Boolean(anchorEl)

  const onClose = () => {
    setAnchorEl(null)
  }

  const sortedPercentages = Object.entries(percentages).sort(
    ([_A, percentageA], [_B, percentageB]) => percentageB - percentageA
  )
  return (
    <>
      <Container
        height={height}
        backgroundColor={backgroundColor}
        maxWidth={maxWidth}
        onMouseEnter={(e) => {
          setAnchorEl(e.currentTarget)
        }}
        onMouseLeave={onClose}>
        {loading && <ProgressBar color={Colors.blueDark} percentage={100} pulsating />}
        {!loading && sortedPercentages.map(renderProgressBar)}
      </Container>
      <Popover
        sx={{ pointerEvents: 'none' }}
        open={showDetails && open && propertiesDistributions.length > 0}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: -8,
          horizontal: 0,
        }}>
        <PropertiesDistributionList>
          {propertiesDistributions.map((item) => (
            <PropertiesDistributionItem
              key={item.type}
              percentage={item.percentage}
              type={item.type}
              value={item.value}
              hideBorder
            />
          ))}
        </PropertiesDistributionList>
      </Popover>
    </>
  )
}

const renderProgressBar = ([color, percentage]: [string, number]) => (
  <ProgressBar key={color} color={color} percentage={percentage * 100} />
)

export const PercentageBarLoading = () => <PercentageBar loading />
